import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import dayjs from 'dayjs';
import { ROOT_URL } from '../../api/ApiConfig';
import {
  applyAnalyticsPendingLoading,
  markAnalyticsFetchKey,
} from '../../utils/analyticsQuery';

/* ----------------------------------------------------------
   ✅ Helper function to detect range type from dates
----------------------------------------------------------- */

const detectRangeType = (startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const now = dayjs();

  if (!start.isValid() || !end.isValid()) return 'custom';

  const diffDays = end.diff(start, 'day');

  // Single day selections
  if (diffDays === 0) {
    if (end.isSame(now, 'day')) return 'today';
    if (end.isSame(now.subtract(1, 'day'), 'day')) return 'yesterday';
    return 'custom';
  }

  // All presets MUST:
  // 1. End exactly today
  // 2. Start exactly at the expected past date
  if (!end.isSame(now, 'day')) return 'custom';

  if (
    diffDays === 6 &&
    start.isSame(now.subtract(6, 'day'), 'day')
  ) {
    return '7d'; // or 'week' if your backend uses 'week'
  }

  if (
    diffDays === 29 &&
    start.isSame(now.subtract(29, 'day'), 'day')
  ) {
    return '30d';
  }

  if (
    diffDays === 89 &&
    start.isSame(now.subtract(89, 'day'), 'day')
  ) {
    return '90d';
  }

  if (
    diffDays === 364 &&
    start.isSame(now.subtract(364, 'day'), 'day')
  ) {
    return '1y';
  }

  // Everything else → custom (even if it's 29 days but wrong start!)
  return 'custom';
};
/* ----------------------------------------------------------
   ✅ Thunk: Fetch Kb Tag Usage Stats
----------------------------------------------------------- */
export const getKbTagUsageStats = createAsyncThunk(
  'KbTagUsageStats/getKbTagUsageStats',
  async (params = {}, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const authToken = state.loginToken?.response?.token;
      const businessId = state.loginToken?.response?.user?.business_id;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Karachi';

      if (!businessId) throw new Error('Business ID not found');

      let startDate = params.startDate;
      let endDate = params.endDate;
      let range = params.range;
      const group_id = params.group_id;

      if (!startDate || !endDate) {
        endDate = dayjs().format('YYYY-MM-DD');
        startDate = dayjs().subtract(6, 'days').format('YYYY-MM-DD');
        range = '7d';
      }

      if (!range) range = detectRangeType(startDate, endDate);

      // ✅ Build manual (non-encoded) query string
      let query = `timezone=${timezone}&range=${range}`;
      if (range === 'custom') {
        query += `&from=${startDate}&to=${endDate}`;
      }
      if (group_id) {
        query += `&group_id=${encodeURIComponent(group_id)}`;
      }

      const url = `${ROOT_URL}/analytics/Kb/article-tags-stats/${businessId}?${query}`;

      console.log('📊 Requesting Tag Usage Stats From:', url);

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${authToken}` },
      });

      return response.data;
    } catch (error) {
      console.error('❌ Error fetching Kb tag usage stats:', error);
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch Kb tag usage stats'
      );
    }
  }
);

/* ----------------------------------------------------------
   ✅ Thunk: Initialize Default Tag Stats (7-Day Range)
----------------------------------------------------------- */
export const initKbTagUsageStats = createAsyncThunk(
  'KbTagUsageStats/initKbTagUsageStats',
  async ({ group_id } = {}, { dispatch }) => {
    const startDate = dayjs().subtract(6, 'day').format('YYYY-MM-DD');
    const endDate = dayjs().format('YYYY-MM-DD');
    await dispatch(getKbTagUsageStats({ startDate, endDate, range: '7d', group_id }));
  }
);

/* ----------------------------------------------------------
   ✅ Initial State
----------------------------------------------------------- */
const initialState = {
  loading: false,
  error: null,
  data: null,
  lastFetchKey: null,
  tagData: [],
  range: '7d',
  dateRange: {
    startDate: dayjs().subtract(6, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  },
};

/* ----------------------------------------------------------
   ✅ Slice Definition
----------------------------------------------------------- */
const KbTagUsageStatsSlice = createSlice({
  name: 'KbTagUsageStats',
  initialState,
  reducers: {
    resetKbTagUsageStats: () => initialState,
    setDateRange: (state, action) => {
      const { startDate, endDate } = action.payload;
      state.dateRange = { startDate, endDate };
      state.range = detectRangeType(startDate, endDate);
    },
    clearError: (state) => {
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(getKbTagUsageStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, action.meta?.arg);
        state.error = null;
      })
      .addCase(getKbTagUsageStats.fulfilled, (state, action) => {
        state.loading = false;
        markAnalyticsFetchKey(state, action.meta?.arg);
        state.error = null;
        const payload = action.payload;

        if (payload?.data) {
          state.data = payload.data;
          state.tagData = payload.data.data || payload.data.tags || [];
          state.range = payload.data.range || state.range;
          state.dateRange = {
            startDate: payload.data.from || state.dateRange.startDate,
            endDate: payload.data.to || state.dateRange.endDate,
          };
        } else if (payload?.tags) {
          state.data = payload;
          state.tagData = payload.tags;
          state.range = payload.range || state.range;
          state.dateRange = {
            startDate: payload.from || state.dateRange.startDate,
            endDate: payload.to || state.dateRange.endDate,
          };
        } else if (Array.isArray(payload)) {
          state.data = { tags: payload };
          state.tagData = payload;
        } else {
          state.data = payload;
          state.tagData = [];
        }

        console.log('✅ Tag usage data loaded:', state.tagData.length, 'tags');
      })
      .addCase(getKbTagUsageStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = null;
        state.tagData = [];
        console.error('❌ Tag usage loading failed:', action.payload);
      })
      .addCase(initKbTagUsageStats.pending, (state, action) => {
        applyAnalyticsPendingLoading(state, { range: '7d', group_id: action.meta?.arg?.group_id });
      })
      .addCase(initKbTagUsageStats.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(initKbTagUsageStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { resetKbTagUsageStats, setDateRange, clearError } =
  KbTagUsageStatsSlice.actions;

export default KbTagUsageStatsSlice.reducer;
